import {
  bookingOnLoad,
  createBooking,
  createBookingEnc,
  requestSecSession,
  verifyInternalUser,
} from 'api/services/Form';
import { bookingOnLoadV2 } from 'api/services/FormV2';
import BookingAvailabilityNotice from 'components/BookingAvailabilityNotice';
import { useError } from 'context/ErrorContext';
import { useSecSession } from 'context/SecSessionContext';
import moment from 'moment-timezone';
import Disable from 'pages/Disable';
import ErrorBoundary from 'pages/ErrorBoundary/ErrorBoundary';
import Validation from 'pages/Validation';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  constructSecurePayload,
  generateRandomString,
  getApiLangParam,
  getLocalizedValue,
  verifySignatureWithPKCS1,
} from 'utils/helper';
import { getLocalizedValueV2 } from 'utils/helperV2';
import Layout from '../../../components/Layout';
import { PAGE } from '../../../entities';
import {
  BookingOnLoadRes,
  CareProvider,
  CreateBookingReqConfig,
  CreateBookingRes,
  Department,
  FORM_FLOW,
  FORM_SECTION,
  InternalBookingAuthValues,
  RequestSecSessionRes,
  ServiceItem,
} from '../../../entities/Form';
import {
  BookingModeV2,
  BookingOnLoadResV2,
  FormValuesV2,
} from '../../../entities/FormV2';
import { AppointmentResult, RESULT } from '../../../entities/Result';
import { canContinueBookingWithNotice, getSystemMessage } from '../../../utils/bookingAvailability';
import Result from '../../Result';
import Form from '../Form/v2';
import InternalValidation from '../InternalValidation';
import PreviewV2 from '../Preview/v2';
import {
  getAggregatedSpecialtyOptionsV2,
  getBookingOnLoadConfig,
  getDoctorSpecialtyOptions,
  getEffectiveSiteId,
  getFixedDepartmentValue,
  getInitialFormSection,
  getMasterServiceItemOptions,
  getSpecialtyDoctorOptionsV2,
  getSpecialtyMasterServiceItemOptionsV2,
  isV2BookingMode,
  postProcessBookingOnLoadSpecialtyOptionsV2,
} from './v2.utils';

interface HomePageProps {
  isInternalBooking?: boolean;
}

type BookingOnLoadState =
  | { version: 'v1'; data: BookingOnLoadRes }
  | { version: 'v2'; data: BookingOnLoadResV2 };

export const getServiceItemsWithProviders = (selectedDepartment?: Department) => {
  if (!selectedDepartment?.careProviders) return [];

  const serviceItemMap = new Map<string, { serviceItem: ServiceItem; careProviders: CareProvider[] }>();

  selectedDepartment.careProviders.forEach((careProvider) => {
    careProvider.serviceItems?.forEach((serviceItem) => {
      if (!serviceItemMap.has(serviceItem.value)) {
        serviceItemMap.set(serviceItem.value, {
          serviceItem,
          careProviders: [],
        });
      }
      serviceItemMap.get(serviceItem.value)?.careProviders.push(careProvider);
    });
  });

  return Array.from(serviceItemMap.values());
};

const defaultValues: FormValuesV2 = {
  departmentValue: undefined,
  departmentHid: undefined,
  specialtyHid: undefined,
  specialtyValue: undefined,
  specialtyOptionKey: undefined,
  doctorPreference: undefined,
  preferredCareProviderHid: undefined,
  masterServiceItemHid: undefined,
  siteCode: undefined,
  serviceItemHid: undefined,
  careProviderHid: undefined,
  fromDt: '',
  lastName: '',
  firstName: '',
  idNo: '',
  countryCode: '852',
  mobileNo: '',
  email: '',
  isJoinedEhealth: '',
  remarks: '',
};

const HomePage: FC<HomePageProps> = ({ isInternalBooking = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { secSession, setSecSession } = useSecSession();
  const { setError } = useError();

  const [mode, setMode] = useState<BookingModeV2>();
  const [page, setPage] = useState(PAGE.FORM);
  const [formValues, setFormValues] = useState<FormValuesV2>(defaultValues);
  const effectiveSiteId = getEffectiveSiteId(mode, formValues.siteCode);
  const [activeFormSection, setActiveFormSection] = useState(FORM_SECTION.DEPARTMENT);
  const [isFormValuesDirty, setIsFormValuesDirty] = useState(false);
  const [result, setResult] = useState<AppointmentResult>();
  const [patientFormNoScroll, setPatientFormNoScroll] = useState(false);
  const [scrollToMobileNo, setScrollToMobileNo] = useState(false);

  const bookingOnLoadRequestIdRef = useRef(0);
  const [bookingOnLoadState, setBookingOnLoadState] = useState<BookingOnLoadState>();
  const [bookingOnLoadDataError, setBookingOnLoadDataError] = useState(false);
  const bookingOnLoadData = bookingOnLoadState?.data;
  const bookingOnLoadDataV2 = bookingOnLoadState?.version === 'v2' ? bookingOnLoadState.data : undefined;
  const bookingOnLoadDataV1 = bookingOnLoadState?.version === 'v1' ? bookingOnLoadState.data : undefined;
  const systemMessage = getSystemMessage(bookingOnLoadData);
  const fixedDepartmentValue = getFixedDepartmentValue(mode);
  const departmentOptions = useMemo(
    () =>
      fixedDepartmentValue
        ? bookingOnLoadDataV1?.departmentOptions?.filter(
            (department) => department.value === fixedDepartmentValue,
          )
        : bookingOnLoadDataV1?.departmentOptions,
    [bookingOnLoadDataV1?.departmentOptions, fixedDepartmentValue],
  );
  const specialtyOptions = bookingOnLoadDataV2?.specialtyOptions;
  const showBookingAvailabilityNotice = canContinueBookingWithNotice(
    systemMessage,
    !!(bookingOnLoadState?.version === 'v2' ? specialtyOptions?.length : departmentOptions?.length),
  );
  const doctorSpecialtyOptions = useMemo(
    () => getDoctorSpecialtyOptions(specialtyOptions),
    [specialtyOptions],
  );
  const selectedDoctorSpecialtyOption = doctorSpecialtyOptions?.find(
    (option) =>
      option.careProvider.value === (formValues.preferredCareProviderHid || formValues.careProviderHid) &&
      option.specialty.value === formValues.specialtyValue,
  );
  const aggregatedSpecialtyOptions = useMemo(
    () => getAggregatedSpecialtyOptionsV2(specialtyOptions),
    [specialtyOptions],
  );
  const selectedSpecialtyOption = aggregatedSpecialtyOptions?.find(
    (option) => option.key === formValues.specialtyOptionKey,
  );
  const specialtyDoctorOptions = useMemo(
    () => getSpecialtyDoctorOptionsV2(selectedSpecialtyOption),
    [selectedSpecialtyOption],
  );
  const selectedMasterServiceItem = (
    mode === BookingModeV2.Doctor
      ? getMasterServiceItemOptions(selectedDoctorSpecialtyOption)
      : mode === BookingModeV2.Specialty
        ? getSpecialtyMasterServiceItemOptionsV2(
            selectedSpecialtyOption,
            formValues.doctorPreference === 'specific' ? formValues.preferredCareProviderHid : undefined,
          )
        : []
  ).find((option) => option.masterServiceItem.hid === formValues.masterServiceItemHid);
  const selectedActualCareProvider =
    mode === BookingModeV2.Doctor
      ? selectedDoctorSpecialtyOption?.careProvider
      : specialtyDoctorOptions.find((option) => option.careProvider.value === formValues.careProviderHid)
          ?.careProvider;
  const selectedMasterServiceItemSource = selectedMasterServiceItem?.sources.find(
    (source) =>
      source.careProvider.value === formValues.careProviderHid &&
      source.department.hid === formValues.departmentHid &&
      source.serviceItem.value === formValues.serviceItemHid,
  );
  const departmentDisplayName = isV2BookingMode(mode)
    ? selectedMasterServiceItemSource?.department.name
    : departmentOptions?.find((department) => department.value === formValues.departmentValue)?.name;
  const careProviderDisplayName =
    mode === BookingModeV2.Doctor && selectedDoctorSpecialtyOption
      ? `${selectedDoctorSpecialtyOption.careProvider.name} - ${
          getLocalizedValueV2(selectedDoctorSpecialtyOption.specialty) ||
          selectedDoctorSpecialtyOption.specialty.nameEn
        }`
      : mode === BookingModeV2.Specialty
        ? selectedActualCareProvider?.name
        : undefined;
  const serviceItemDisplayName = isV2BookingMode(mode)
    ? getLocalizedValueV2(selectedMasterServiceItem?.masterServiceItem)
    : undefined;
  const careProviderOptions = bookingOnLoadDataV1
    ? departmentOptions?.find((opt) => opt.value === formValues.departmentValue)?.careProviders || []
    : undefined;
  const serviceItemOptions = bookingOnLoadDataV1
    ? careProviderOptions?.find((opt) => opt.value === formValues.careProviderHid)?.serviceItems || []
    : undefined;
  const departmentFlow = formValues.departmentValue
    ? departmentOptions?.find((opt) => opt.value === formValues.departmentValue)?.flow
    : undefined;
  const flow = isV2BookingMode(mode) ? FORM_FLOW.DEFAULT : departmentFlow;
  const [bookingTimeLabel, setBookingTimeLabel] = useState<string>();

  // Flow 2
  const serviceItemsWithProviders = useMemo(
    () => getServiceItemsWithProviders(departmentOptions?.find((opt) => opt.value === formValues.departmentValue)),
    [departmentOptions, formValues.departmentValue],
  );

  const submit = (otpSid?: string, onError?: () => void) => {
    const {
      departmentValue,
      serviceItemHid,
      careProviderHid,
      fromDt,
      toDt,
      lastName,
      firstName,
      idNo,
      countryCode,
      mobileNo,
      email,
      isJoinedEhealth,
      remarks,
    } = formValues;
    const payload = {
      departmentHid:
        formValues.departmentHid || departmentOptions?.find((dept) => dept.value === departmentValue)?.hid,
      serviceItemHid,
      careProviderHid,
      fromDt,
      toDt,
      lastName,
      firstName,
      idNo,
      countryCode,
      mobileNo,
      email,
      isJoinedEhealth: isJoinedEhealth === 'Y',
      remarks,
      idType: !!idNo ? 'HKID' : undefined,
      otpSid,
    } as CreateBookingReqConfig;
    const onCreateBookingSuccess = (res: CreateBookingRes) => {
      const { bookingNo, isRequireOtp } = res;
      if (isRequireOtp) {
        if (otpSid) {
          onError?.();
          setError({
            message: i18n.t('systemErrorDetail'),
          });
        } else {
          setPage(PAGE.VALIDATION);
        }
      } else {
        const { enquiryPhoneNo, isConfirmed } = res;
        const locationName = getLocalizedValue({
          nameSc: res.locationNameSc,
          nameTc: res.locationNameTc,
          name: res.locationName,
        });

        const bookingTime = getLocalizedValue({
          nameSc: res.bookingTimeLabelSc,
          nameTc: res.bookingTimeLabelTc,
          name: res.bookingTimeLabel,
        });

        setPage(PAGE.RESULT);
        setResult(
          bookingNo
            ? {
                status: isConfirmed ? RESULT.SUCCESS : RESULT.PENDING,
                bookingNo,
                locationName,
                bookingTime,
                enquiryPhoneNo,
              }
            : {
                status: RESULT.FAIL,
                locationName,
                bookingTime,
                enquiryPhoneNo,
              },
        );
      }
    };

    if (bookingOnLoadData?.enc) {
      const onCreateBookingEnc = async (secSession: RequestSecSessionRes) =>
        createBookingEnc(
          await constructSecurePayload({ lang: getApiLangParam(i18n.language), ...payload }, secSession.pk),
          onCreateBookingSuccess,
          onError,
        );

      const onSecSession = async (secSession: RequestSecSessionRes) => {
        if (secSession.sign && process.env.REACT_APP_SERVER_PUBLIC_CERT) {
          const isValid = await verifySignatureWithPKCS1(
            process.env.REACT_APP_SERVER_PUBLIC_CERT,
            secSession.pk,
            secSession.sign,
          );
          if (!isValid) {
            setError({
              message: i18n.t('securityWarningMessage'),
              confirmTitle: i18n.t('reload'),
              onConfirm: () => navigate(0),
            });
            console.error('Signature is invalid!');
            return;
          }
        }
        onCreateBookingEnc(secSession);
      };

      !secSession
        ? requestSecSession(
            { k: generateRandomString() },
            async (secSession) => {
              setSecSession(secSession);
              onSecSession(secSession);
            },
            () => setSecSession(null),
          )
        : onCreateBookingEnc(secSession);
    } else {
      setSecSession(null);
      createBooking(payload, onCreateBookingSuccess, onError);
    }
  };

  const verifyInternalUserAndSubmit = (
    internalAuthValues: InternalBookingAuthValues,
    onInvalid: () => void,
    onError: () => void,
  ) => {
    const { countryCode, mobileNo } = formValues;
    if (!countryCode || !mobileNo) {
      onError();
      setError({
        message: i18n.t('systemErrorDetail'),
      });
      return;
    }

    verifyInternalUser(
      {
        countryCode,
        mobileNo,
        accountName: internalAuthValues.accountName,
        password: internalAuthValues.password,
      },
      ({ sid, isSuccess }) => {
        if (!isSuccess) {
          onInvalid();
          return;
        }

        submit(sid, onError);
      },
      onError,
    );
  };

  const init = () => {
    setMode(undefined);
    setPage(PAGE.FORM);
    setFormValues(defaultValues);
    setActiveFormSection(FORM_SECTION.DEPARTMENT);
    setBookingOnLoadState(undefined);
    setBookingOnLoadDataError(false);
    setBookingTimeLabel(undefined);
  };

  const backToBookingMode = () => {
    setMode(undefined);
    setFormValues(defaultValues);
    setActiveFormSection(FORM_SECTION.DEPARTMENT);
    setBookingOnLoadState(undefined);
    setBookingOnLoadDataError(false);
    setBookingTimeLabel(undefined);
    setIsFormValuesDirty(false);
    setPatientFormNoScroll(false);
    setScrollToMobileNo(false);
  };

  const selectBookingMode = (nextMode: BookingModeV2) => {
    setMode(nextMode);
    setFormValues(defaultValues);
    setActiveFormSection(getInitialFormSection(nextMode));
    setBookingTimeLabel(undefined);
    setIsFormValuesDirty(false);
  };

  useEffect(() => {
    const requestId = ++bookingOnLoadRequestIdRef.current;
    const cleanup = () => {
      if (bookingOnLoadRequestIdRef.current === requestId) {
        bookingOnLoadRequestIdRef.current += 1;
      }
    };

    if (!mode) {
      setBookingOnLoadState(undefined);
      return cleanup;
    }

    setBookingOnLoadState(undefined);
    setBookingOnLoadDataError(false);

    const onError = () => {
      if (bookingOnLoadRequestIdRef.current !== requestId) return;
      setBookingOnLoadDataError(true);
    };

    const loadConfig = getBookingOnLoadConfig(mode);
    if (!loadConfig) return cleanup;

    if (loadConfig.version === 'v2') {
      bookingOnLoadV2(
        { cmsSiteId: loadConfig.cmsSiteId },
        (data) => {
          if (bookingOnLoadRequestIdRef.current === requestId) {
            setBookingOnLoadState({
              version: 'v2',
              data: {
                ...data,
                specialtyOptions: postProcessBookingOnLoadSpecialtyOptionsV2(
                  data.specialtyOptions,
                  mode,
                ),
              },
            });
          }
        },
        onError,
      );
      return cleanup;
    }

    bookingOnLoad(
      { cmsSiteId: loadConfig.cmsSiteId },
      (data) => {
        if (bookingOnLoadRequestIdRef.current !== requestId) return;

        const fixedDepartment = data.departmentOptions?.find(
          (department) => department.value === fixedDepartmentValue,
        );
        const message = getSystemMessage(data);
        if (message && !canContinueBookingWithNotice(message, !!fixedDepartment)) {
          setBookingOnLoadState({ version: 'v1', data });
          return;
        }

        if (fixedDepartmentValue) {
          if (!fixedDepartment) {
            setError({
              title: t('serviceUnavailable'),
              message: t('currentServiceUnavailable'),
              onConfirm: () => {
                setError(null);
                backToBookingMode();
              },
            });
            return;
          }

          setFormValues({ ...defaultValues, departmentValue: fixedDepartment.value });
          setActiveFormSection(
            fixedDepartment.flow === FORM_FLOW.DEFAULT
              ? FORM_SECTION.DOCTOR
              : FORM_SECTION.SERVICE,
          );
        }

        setBookingOnLoadState({ version: 'v1', data });
      },
      onError,
    );
    return cleanup;
  }, [fixedDepartmentValue, mode, setError, t]);

  useEffect(() => {
    // window.scrollTo(0, 0);
    // For ios safari scrollTo bug
    setTimeout(() => window.scroll({ top: -1, left: 0 }), 10);

    // Prevent keyframe animation happen at the start
    document.body.className = 'preload';
    const timer = setTimeout(() => document.body.classList.remove('preload'), 300);

    return () => clearTimeout(timer);
  }, [page]);

  useEffect(() => {
    const mobileNoElement = document.getElementsByName('mobileNo')[0] as HTMLInputElement | undefined;
    if (scrollToMobileNo && mobileNoElement) {
      mobileNoElement.focus({ preventScroll: true });
      setTimeout(() => {
        window.scrollTo({
          top: mobileNoElement.getBoundingClientRect().top + window.scrollY - 46,
          behavior: 'smooth',
        });
        setPatientFormNoScroll(false);
      }, 750);
      setScrollToMobileNo(false);
    }
  }, [document.getElementsByName('mobileNo')[0], scrollToMobileNo]);

  useEffect(() => {
    const handleBeforeUnload = () => window.scrollTo(0, 0);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    moment.tz.setDefault('Asia/Hong_Kong');
  }, []);

  return (
    <Layout
      isFormValuesDirty={isFormValuesDirty}
      backgroundVariant={isInternalBooking ? 'internal' : 'default'}
      logoVariant={
        mode === BookingModeV2.EKMC
          ? 'ekmc'
          : mode === BookingModeV2.BH
            ? 'bh'
            : 'bh-ekmc'
      }
    >
      {systemMessage && !showBookingAvailabilityNotice ? (
        <Disable systemMessage={systemMessage} />
      ) : bookingOnLoadDataError ? (
        <ErrorBoundary />
      ) : (
        <>
          {page === PAGE.FORM && (
            <>
              {systemMessage && showBookingAvailabilityNotice && (
                <BookingAvailabilityNotice message={systemMessage} />
              )}
              <Form
                key={mode ?? 'booking-mode'}
                mode={mode}
                onSelectMode={selectBookingMode}
                onBackToBookingMode={backToBookingMode}
                flow={flow}
                effectiveSiteId={effectiveSiteId}
                specialtyOptions={specialtyOptions}
                departmentOptions={departmentOptions}
                careProviderOptions={careProviderOptions}
                serviceItemOptions={
                  formValues.departmentValue && flow === FORM_FLOW.DOCTOR_LAST
                    ? serviceItemsWithProviders
                    : serviceItemOptions
                }
                formValues={formValues}
                onChangeFormValues={setFormValues}
                activeFormSection={activeFormSection}
                onSubmit={() => setPage(PAGE.PREVIEW)}
                patientFormNoScroll={patientFormNoScroll}
                bookingTimeLabel={bookingTimeLabel}
                onSelectTime={setBookingTimeLabel}
                setIsFormValuesDirty={setIsFormValuesDirty}
              />
            </>
          )}
          {page === PAGE.PREVIEW && bookingTimeLabel && (
            <PreviewV2
              departmentOptions={departmentOptions}
              careProviderOptions={careProviderOptions}
              serviceItemOptions={serviceItemOptions}
              bookingTimeLabel={bookingTimeLabel}
              departmentDisplayName={departmentDisplayName}
              careProviderDisplayName={careProviderDisplayName}
              serviceItemDisplayName={serviceItemDisplayName}
              values={formValues}
              onSubmit={(onError) => {
                if (isInternalBooking) {
                  setPage(PAGE.INTERNAL_VALIDATION);
                  return;
                }
                submit(undefined, onError);
              }}
              onBack={() => {
                setActiveFormSection(FORM_SECTION.PATIENT_FORM);
                setPage(PAGE.FORM);
              }}
            />
          )}
          {page === PAGE.VALIDATION && formValues.mobileNo && formValues.countryCode && (
            <Validation
              type="create"
              onSubmit={submit}
              onBack={() => {
                setPatientFormNoScroll(true);
                setScrollToMobileNo(true);
                setActiveFormSection(FORM_SECTION.PATIENT_FORM);
                setPage(PAGE.FORM);
              }}
              mobileNo={formValues.mobileNo}
              countryCode={formValues.countryCode}
            />
          )}
          {page === PAGE.INTERNAL_VALIDATION && (
            <InternalValidation
              onSubmit={verifyInternalUserAndSubmit}
              onBack={() => {
                setActiveFormSection(FORM_SECTION.PATIENT_FORM);
                setPage(PAGE.FORM);
              }}
            />
          )}
          {page === PAGE.RESULT && result && (
            <Result
              result={result}
              effectiveSiteId={effectiveSiteId}
              onReStart={init}
              onEdit={() => {
                setActiveFormSection(FORM_SECTION.DATE_TIME);
                setPage(PAGE.FORM);
              }}
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default HomePage;
